// poe2scout 取得（全て Worker プロキシ経由, §6）。
// セッション開始時と明示的な再取得時のみ呼ぶ（定期ポーリングしない, §6.3）。
import { MARKET_CATEGORIES, PROXY_BASE_URL, REALM, UNIQUE_CATEGORIES } from '../config';
import type {
  CatalogItem,
  CurrenciesResponse,
  Poe2ScoutCurrency,
  Poe2ScoutLeague,
  UniquesResponse,
} from './types';

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${PROXY_BASE_URL}${path}`, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`取得失敗 ${path}: ${res.status}`);
  return (await res.json()) as T;
}

/** リーグ一覧（§6.2 step1）。現行は IsCurrent で選ぶ。 */
export function fetchLeagues(): Promise<Poe2ScoutLeague[]> {
  return getJson<Poe2ScoutLeague[]>(`/${REALM}/Leagues`);
}

/** 指定リーグの単一カテゴリのアイテム一覧（PerPage=200 で1カテゴリを1リクエスト）。 */
export async function fetchCurrencies(leagueName: string, category = 'currency'): Promise<Poe2ScoutCurrency[]> {
  const enc = encodeURIComponent(leagueName);
  const data = await getJson<CurrenciesResponse>(
    `/${REALM}/Leagues/${enc}/Currencies/ByCategory?Category=${category}&PerPage=200`,
  );
  return data.Items;
}

/**
 * market カタログ全体を取得（§6.2）。currency 系の全カテゴリを並列取得して結合する。
 * オーブ＋入場券＋素材など、API で価格が付く品をすべて含む。1カテゴリ=1リクエスト
 * （行数には依存しない＝§6.3 の趣旨は維持。セッション開始時と明示的再取得時のみ）。
 */
async function fetchCurrencyCatalog(leagueName: string): Promise<CatalogItem[]> {
  const enc = encodeURIComponent(leagueName);
  const lists = await Promise.all(
    MARKET_CATEGORIES.map(async (cat) => {
      try {
        const data = await getJson<CurrenciesResponse>(
          `/${REALM}/Leagues/${enc}/Currencies/ByCategory?Category=${cat.id}&PerPage=200`,
        );
        return data.Items.map((item) => ({ item, category: cat.id, categoryLabel: cat.label }));
      } catch {
        return [] as CatalogItem[];
      }
    }),
  );
  return lists.flat();
}

/** ユニークアイテムを正規化して取得（ApiId 無し → `u:<UniqueItemId>`）。 */
async function fetchUniqueCatalog(leagueName: string): Promise<CatalogItem[]> {
  const enc = encodeURIComponent(leagueName);
  const lists = await Promise.all(
    UNIQUE_CATEGORIES.map(async (cat) => {
      try {
        // Uniques は PerPage 上限が 250（300以上は 422）。250品超のカテゴリは高額順 先頭250件。
        const data = await getJson<UniquesResponse>(
          `/${REALM}/Leagues/${enc}/Uniques/ByCategory?Category=${cat.id}&PerPage=250`,
        );
        return data.Items.map((u) => ({
          item: { ApiId: `u:${u.UniqueItemId}`, Text: u.Text, IconUrl: u.IconUrl, PriceLogs: u.PriceLogs },
          category: `u:${cat.id}`,
          categoryLabel: cat.label,
        }));
      } catch {
        return [] as CatalogItem[];
      }
    }),
  );
  return lists.flat();
}

/** market カタログ全体（通貨系 + ユニーク）。 */
export async function fetchCatalog(leagueName: string): Promise<CatalogItem[]> {
  const [currencies, uniques] = await Promise.all([
    fetchCurrencyCatalog(leagueName),
    fetchUniqueCatalog(leagueName),
  ]);
  return [...currencies, ...uniques];
}
